import React from "react";
import { Badge, Box, Divider, IconButton, Menu, Stack, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SaveIcon from "@mui/icons-material/Save";
import i18next from "i18next";
import SaitButton from "../atomic/SaitButton";
import SaitTextField from "../atomic/SaitTextField";
import { tableCellClasses } from "@mui/material/TableCell";

export default function GridToolbarFilterButton(props) {
	const [newReportName, setNewReportName] = React.useState("");

	const [gridFiltersAnchorEl, setGridFiltersAnchorEl] = React.useState(null);

	const openGridFilters = Boolean(gridFiltersAnchorEl);

	const handleOpenFilters = (event) => {
		setGridFiltersAnchorEl(event.currentTarget);
	};
	const handleCloseFilters = () => {
		setGridFiltersAnchorEl(null);
	};

	const StyledTableCell = styled(TableCell)(({ theme }) => ({
		[`&.${tableCellClasses.head}`]: {
			backgroundColor: theme.palette.primary.light,
		},
		[`&.${tableCellClasses.body}`]: {
			fontSize: 14,
		},
	}));

	function deleteGridFilter(filterId) {
		let updatedFilters = [...props.gridFilters];
		updatedFilters = updatedFilters.filter((filter) => filter.id !== filterId);
		props.changeGridFilters?.(updatedFilters);
	}

	function getColumnLabelFromField(field) {
		return props.columnsGrid.find((column) => column.field === field)["headerName"];
	}

	function getColumnTypeFromField(field) {
		return props.columnsGrid.find((column) => column.field === field)["type"];
	}

	function getGridFilterStringRepresentation(field, value) {
		if (Array.isArray(value)) {
			return value.join(", ");
		} else {
			let filterType = getColumnTypeFromField(field);
			switch (filterType) {
				case "date":
					if (typeof value === "string") {
						//se valore è stringa sto leggendo il valore dal filtro salvato in LocalStorage
						value = new Date(value);
					}
					return value.toLocaleDateString();
				case "dateTime":
					if (typeof value === "string") {
						value = new Date(value);
					}
					return value.toLocaleString();
				default:
					return value;
			}
		}
	}

	function getLocaleOperator(operator) {
		switch (operator) {
			case "contains":
				return i18next.t("contains");
			case "doesNotContain":
				return i18next.t("not_contains");
			case "equals":
				return i18next.t("equalsTo");
			case "startsWith":
				return i18next.t("startsWith");
			case "endsWith":
				return i18next.t("endsWith");
			case "isEmpty":
				return i18next.t("isEmpty");
			case "isNotEmpty":
				return i18next.t("isNotEmpty");
			case "isAnyOf":
				return i18next.t("isAnyOf");
			case "=":
				return "=";
			case "!=":
				return "!=";
			case ">":
				return ">";
			case ">=":
				return ">=";
			case "<":
				return "<";
			case "<=":
				return "<=";
			case "is":
				return i18next.t("is");
			case "not":
				return i18next.t("is_not");
			case "after":
				return i18next.t("after_the");
			case "onOrAfter":
				return i18next.t("from_the");
			case "before":
				return i18next.t("before_the");
			case "onOrBefore":
				return i18next.t("to_the");

			default:
				return i18next.t("contains");
		}
	}

	return (
		<>
			<Badge
				badgeContent={props.gridFilters?.length}
				color="primary"
				overlap="circular"
				sx={{
					"& .MuiBadge-badge": {
						top: 9, // o un valore negativo per farlo scendere
						left: 25,
					},
				}}
				anchorOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				{props.toolbarIcons ? (
					<IconButton loading={props.isFiltering} onClick={handleOpenFilters}>
						<FilterAltIcon color="primary" />
					</IconButton>
				) : (
					<SaitButton
						text={i18next.t("filters")}
						loading={props.isFiltering}
						onClick={handleOpenFilters}
						startIcon={<FilterAltIcon />}
						variant="text"
						// disabled={props.gridFilters.length === 0}
					/>
				)}
			</Badge>
			<Menu anchorEl={gridFiltersAnchorEl} open={openGridFilters} onClose={handleCloseFilters} sx={{ minHeight: 900 }}>
				<Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
					<TableContainer sx={{ p: 1 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<StyledTableCell>{i18next.t("column")}</StyledTableCell>
									<StyledTableCell>{i18next.t("operator")}</StyledTableCell>
									<StyledTableCell>{i18next.t("value")}</StyledTableCell>
									<StyledTableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{props.gridFilters?.map((gridFilter, index) => (
									<TableRow key={gridFilter.id}>
										<TableCell>{getColumnLabelFromField(gridFilter.field)}</TableCell>
										<TableCell width={110}>{getLocaleOperator(gridFilter.operator)}</TableCell>
										<TableCell>{getGridFilterStringRepresentation(gridFilter.field, gridFilter.value)}</TableCell>
										<TableCell width={10}>
											<IconButton
												size="small"
												color="error"
												onClick={() => {
													deleteGridFilter(gridFilter.id);
												}}
											>
												<DeleteIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<Stack width={400} direction="column" spacing={1} divider={<Divider orientation="horizontal" flexItem />} sx={{ p: 1 }}>
						<Box display="flex" justifyContent="center" alignItems="center">
							<SaitTextField label={i18next.t("filter_name")} value={newReportName} onChange={(e) => setNewReportName(e.target.value)} sx={{ pr: 1 }} />
							<SaitButton onClick={() => props.setNewReportName(newReportName)} icon={<SaveIcon />} disabled={!newReportName} />
						</Box>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<StyledTableCell />
										<StyledTableCell>{i18next.t("filter")}</StyledTableCell>
										<StyledTableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{props.savedFilters?.map((savedFilter, index) => (
										<TableRow key={index}>
											<TableCell width={10}>
												<Tooltip title={i18next.t("apply")}>
													<IconButton
														size="small"
														color="success"
														onClick={() => {
															props.applySavedFilters(savedFilter.filters);
														}}
													>
														<TaskAltIcon />
													</IconButton>
												</Tooltip>
											</TableCell>
											<TableCell>{savedFilter.name}</TableCell>
											<TableCell width={10}>
												<IconButton
													size="small"
													color="error"
													onClick={() => {
														props.deleteSavedFilter(savedFilter.name);
													}}
												>
													<DeleteIcon />
												</IconButton>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Stack>
				</Stack>
			</Menu>
		</>
	);
}
